import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ScaledImage from './ScaledImage';
import './StreamingText.css';

interface StreamingTextProps {
  content: string;
  isComplete?: boolean;
  className?: string;
  imageMaxWidth?: string;
}

/**
 * Remove duplicate inline images so each image renders at most once per message.
 * Keeps the first occurrence of each image URL (compared without its SAS/query
 * string) and strips any later `![alt](url)` referencing the same image. This is
 * the deterministic, agent-agnostic guarantee for the once-per-message contract —
 * the LLM may re-type the same image URL across steps; this enforces uniqueness at
 * render time. See docs/standards/agent-image-citation-contract.md §4.
 */
export function dedupeImageMarkdown(markdown: string): string {
  if (!markdown || markdown.indexOf('![') === -1) return markdown;
  const seen = new Set<string>();
  return markdown.replace(/!\[[^\]]*\]\(([^)]+)\)/g, (match, rawUrl) => {
    const key = String(rawUrl).split('?')[0].trim();
    if (!key) return match;
    if (seen.has(key)) return '';
    seen.add(key);
    return match;
  });
}

const StreamingText: React.FC<StreamingTextProps> = ({ 
  content, 
  isComplete = false, 
  className = '',
  imageMaxWidth = '280px'
}) => {
  const [visibleLength, setVisibleLength] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousContentRef = useRef('');
  const animationRef = useRef<number | null>(null);

  // Memoize markdown components to prevent recreation on every render
  const markdownComponents = useMemo(() => {
    return {
      p: ({ children }: any) => <p className="markdown-paragraph">{children}</p>,
      strong: ({ children }: any) => <strong className="markdown-bold">{children}</strong>,
      em: ({ children }: any) => <em className="markdown-italic">{children}</em>,
      code: ({ children }: any) => <code className="markdown-inline-code">{children}</code>,
      pre: ({ children }: any) => <pre className="markdown-code-block">{children}</pre>,
      blockquote: ({ children }: any) => <blockquote className="markdown-blockquote">{children}</blockquote>,
      h1: ({ children }: any) => <h1 className="markdown-h1">{children}</h1>,
      h2: ({ children }: any) => <h2 className="markdown-h2">{children}</h2>,
      h3: ({ children }: any) => <h3 className="markdown-h3">{children}</h3>,
      ul: ({ children }: any) => <ul className="markdown-list">{children}</ul>,
      ol: ({ children }: any) => <ol className="markdown-ordered-list">{children}</ol>,
      li: ({ children }: any) => <li className="markdown-list-item">{children}</li>,
      // Hide checkbox inputs from GFM task lists (they appear as empty gray squares)
      input: () => null,
      a: ({ href, children }: any) => (
        <a href={href} className="markdown-link" target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
      img: ({ src, alt }: any) => {
        // Handle broken/inaccessible images
        if (!src || src.includes('[Image not accessible:')) {
          return null;
        }

        // Filter out placeholder images (video clapperboards, etc.)
        const srcLower = src.toLowerCase();
        if (srcLower.includes('clapper') || srcLower.includes('video_placeholder') || srcLower.includes('microlearning')) {
          return null; // Don't render placeholder images
        }

        // Convert blob/SharePoint URLs to use image proxy for proper authentication
        let proxiedSrc = src;
        
        // Ensure URL has https:// protocol (Azure Search sometimes returns URLs without protocol)
        if (src && !src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
          proxiedSrc = `https://${src}`;
        }
        
        // Already proxied by the agent backend? Keep as-is
        const isAlreadyProxied = proxiedSrc.includes('/api/image-proxy');
        
        if (!isAlreadyProxied) {
          if (proxiedSrc.includes('blob.core.windows.net')) {
            // Blob URL → always proxy. A SAS token isn't enough when the storage
            // account is behind a network firewall (e.g. prod document-images),
            // so the server-side proxy fetches it with the account key instead.
            const encodedUrl = encodeURIComponent(proxiedSrc);
            proxiedSrc = `${process.env.REACT_APP_API_URL || ''}/api/image-proxy?imageUrl=${encodedUrl}`;
          } else if (proxiedSrc.includes('.sharepoint.com/')) {
            // SharePoint URL → proxy via Express (Graph API sharing links)
            const encodedUrl = encodeURIComponent(proxiedSrc);
            proxiedSrc = `${process.env.REACT_APP_API_URL || ''}/api/image-proxy?imageUrl=${encodedUrl}`;
          }
        }

        // Check if this is a product image (from agent-product-images container)
        // Only product catalog images should render normally, not with ScaledImage
        // Documentation images (owensboro2, unifirst_docs, etc.) should use ScaledImage
        const isProductImage = proxiedSrc.includes('agent-product-images');
        
        // Hide all images if imageMaxWidth is 'hidden'
        if (imageMaxWidth === 'hidden') {
          return null;
        }
        
        if (isProductImage) {
          // Render product catalog images normally (for product cards)
          return (
            <img
              src={proxiedSrc}
              alt={alt || 'Product image'}
              className="max-w-full h-auto rounded-lg"
              loading="lazy"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                console.warn('Failed to load streaming product image:', src);
              }}
            />
          );
        }

        // Use ScaledImage component for documentation images only
        return <ScaledImage src={proxiedSrc} alt={alt || 'Image'} maxWidth={imageMaxWidth} />;
      }
    };
  }, [imageMaxWidth]);

  // Animate content reveal progressively
  const animateContentReveal = useCallback((targetLength: number) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startLength = visibleLength;
    const startTime = Date.now();
    const duration = Math.min(1000, Math.max(200, (targetLength - startLength) * 15)); // 15ms per character, min 200ms, max 1000ms

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Use easeOutCubic for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const currentLength = Math.floor(startLength + (targetLength - startLength) * easeProgress);
      
      setVisibleLength(currentLength);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    setIsAnimating(true);
    animationRef.current = requestAnimationFrame(animate);
  }, [visibleLength]);

  // Track content changes and animate new content
  useEffect(() => {
    const newContent = content.trim();
    const previousContent = previousContentRef.current.trim();

    if (newContent.length > previousContent.length) {
     ;
      
      // Animate to reveal the new content length
      animateContentReveal(newContent.length);
    } else if (newContent.length < previousContent.length) {
      // Handle content reset (like regeneration)
      setVisibleLength(0);
      setTimeout(() => animateContentReveal(newContent.length), 50);
    }

    previousContentRef.current = newContent;
  }, [content, animateContentReveal]);

  // Handle completion
  useEffect(() => {
    if (isComplete && visibleLength < content.length) {
      animateContentReveal(content.length);
    } else if (isComplete) {
      setIsAnimating(false);
    }
  }, [isComplete, content.length, visibleLength, animateContentReveal]);

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Create truncated content for progressive reveal
  // IMPORTANT: Don't truncate in the middle of markdown image syntax
  const displayContent = useMemo(() => {
    let truncated = content.slice(0, visibleLength);
    // (image de-dup is applied after truncation, see dedupedContent)
    
    // Check if we've truncated in the middle of a markdown image: ![alt](url)
    // Look backwards from the truncation point to see if we're inside an incomplete image tag
    const lastImageStart = truncated.lastIndexOf('![');
    if (lastImageStart !== -1) {
      // Check if this image tag is incomplete (no closing paren after the URL)
      const afterImageStart = truncated.slice(lastImageStart);
      const hasCompleteImage = /!\[([^\]]*)\]\(([^)]+)\)/.test(afterImageStart);
      
      if (!hasCompleteImage) {
        // We're in the middle of an image tag. Check if the complete image exists in the full content
        const remainingContent = content.slice(lastImageStart);
        const completeImageMatch = /^!\[([^\]]*)\]\(([^)]+)\)/.exec(remainingContent);
        
        if (completeImageMatch) {
          // Include the complete image in the truncated content
          const completeImageEnd = lastImageStart + completeImageMatch[0].length;
          if (completeImageEnd <= content.length) {
            truncated = content.slice(0, Math.max(visibleLength, completeImageEnd));
          }
        } else {
          // Image is incomplete in source, truncate before the image marker to avoid broken markdown
          truncated = truncated.slice(0, lastImageStart);
        }
      }
    }
    
    // Enforce once-per-message image rendering deterministically.
    return dedupeImageMarkdown(truncated);
  }, [content, visibleLength]);
  
  // Check if content contains large JSON blocks that could impact performance
  const containsLargeJson = useMemo(() => {
    // Look for JSON code blocks that are likely product lists
    const jsonBlockRegex = /```json[\s\S]*?"type"\s*:\s*"product_(?:display|detail)"[\s\S]*?```/i;
    return jsonBlockRegex.test(displayContent) && displayContent.length > 5000;
  }, [displayContent]);
  
  // Separate markdown content and large JSON blocks for optimized rendering
  const [mainContent, jsonBlocks] = useMemo(() => {
    if (!containsLargeJson) {
      return [displayContent, null];
    }
    
    // Split content into sections before JSON blocks and the JSON blocks themselves
    const parts = displayContent.split(/(```json[\s\S]*?```)/g);
    const jsonParts: string[] = [];
    const textParts: string[] = [];
    
    parts.forEach((part, index) => {
      if (part.startsWith('```json') && part.endsWith('```')) {
        jsonParts.push(part);
        // Add a placeholder where the JSON block should be
        textParts.push(`[JSON_BLOCK_${jsonParts.length - 1}]`);
      } else {
        textParts.push(part);
      }
    });
    
    return [textParts.join(''), jsonParts];
  }, [displayContent, containsLargeJson]);

  // Render JSON blocks separately with minimal styling
  const renderJsonBlocks = () => {
    if (!jsonBlocks || jsonBlocks.length === 0) return null;
    
    return jsonBlocks.map((block, index) => {
      // Extract only the code content without the markdown code fence
      const jsonContent = block.replace(/```json\s*/, '').replace(/\s*```$/, '');
      
      return (
        <div 
          key={`json-block-${index}`}
          id={`json-block-${index}`}
          className="json-block overflow-x-auto my-2"
          style={{ 
            maxWidth: '100%', 
            fontSize: '0.85rem',
            opacity: 0.8,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap'
          }}
        >
          <pre className="bg-gray-50 rounded p-2 border border-gray-200">
            <code>{jsonContent}</code>
          </pre>
        </div>
      );
    });
  };

  return (
    <span className={`streaming-text-container ${className} ${isComplete ? 'completed' : ''}`}>
      <div className={`streaming-markdown-content ${isAnimating ? 'revealing' : ''}`}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {mainContent}
        </ReactMarkdown>
        
        {/* Render JSON blocks separately for better performance */}
        {containsLargeJson && renderJsonBlocks()}
      </div>
      {isAnimating && !isComplete && (
        <span className="streaming-cursor" aria-hidden="true">
          ▋
        </span>
      )}
    </span>
  );
};

export default StreamingText; 